import { AdditionalActivatedChallengeFields, ChallengeMessage, ChallengeType, CreateActivatedChallengeRequest, CreateActivatedChallengeResponse, CreateChallengeCheckin, CreateChallengeRequest, CreateChallengeResponse, CreateCheckinResponse, GetActivatedChallengeResponse, GetChallengeResponse, UpdateActivatedChallengeRequest } from '../../types';
import { buildQueryString, del, emptyPost, emptyPut, get, logWithAmplitude, post, put, throwHttpError } from './api';

import { getMainPage } from '../page-name';
import translationData from '../state/translation-data';
import { getStoredReferralCode } from './referral-api';
import { undefinedIfEmpty } from '../utils';

export async function getChallenge(
    amplitude_callsite: string,
    challenge_id: string,
    topic_key?: string,
    activated_challenge_id?: string,
    activated_challenge_key?: string
) {
    // First attempt with all parameters
    try {
        const queryString = buildQueryString({
            topic_key,
            activated_challenge_id: undefinedIfEmpty(activated_challenge_id),
            activated_challenge_key: undefinedIfEmpty(activated_challenge_key)
        });

        const response = await fetch(`/api/challenges/${challenge_id}${queryString}`, get());

        if (response.ok) {
            logWithAmplitude(amplitude_callsite, 'Get Challenge', {
                challenge_id,
                retry_attempt: false
            });

            return await (response.json() as Promise<GetChallengeResponse>);
        }

        // If first attempt failed, throw to trigger retry
        await throwHttpError('First attempt failed, retrying without activated challenge parameters.', response);
    } catch (error) {
        // Second attempt without activated challenge parameters
        console.warn('First getChallenge attempt failed, retrying without activated challenge parameters:', error);

        const queryString = buildQueryString({ topic_key });
        const response = await fetch(`/api/challenges/${challenge_id}${queryString}`, get());

        if (!response.ok) {
            await throwHttpError('An error occurred while getting the challenge on retry.', response);
        }

        logWithAmplitude(amplitude_callsite, 'Get Challenge', {
            challenge_id,
            retry_attempt: true
        });

        return await (response.json() as Promise<GetChallengeResponse>);
    }
}

export async function getChallenges(
    amplitude_callsite: string,
    topic_id: string,
    topic_key?: string,
    activated_challenge_id?: string,
    activated_challenge_key?: string,
) {
    // First attempt with all parameters
    try {
        const queryString = buildQueryString({
            topic_key,
            activated_challenge_id: undefinedIfEmpty(activated_challenge_id),
            activated_challenge_key: undefinedIfEmpty(activated_challenge_key)
        });

        const response = await fetch(`/api/topics/${topic_id}/challenges${queryString}`, get());

        if (response.ok) {
            logWithAmplitude(amplitude_callsite, 'Get Challenge', {
                topic_id,
                retry_attempt: false
            });

            return await (response.json() as Promise<GetChallengeResponse[]>);
        }

        // If first attempt failed, throw to trigger retry
        await throwHttpError('First attempt failed, retrying without activated challenge parameters.', response);
    } catch (error) {
        // Second attempt without activated challenge parameters
        console.warn('First getChallenges attempt failed, retrying without activated challenge parameters:', error);

        const queryString = buildQueryString({ topic_key });
        const response = await fetch(`/api/topics/${topic_id}/challenges${queryString}`, get());

        if (!response.ok) {
            await throwHttpError('An error occurred while getting the challenges on retry.', response);
        }

        logWithAmplitude(amplitude_callsite, 'Get Challenges', {
            topic_id,
            retry_attempt: true
        });

        return await (response.json() as Promise<GetChallengeResponse[]>);
    }
}

export async function getChallenePageByJoinCode(
    amplitude_callsite: string,
    join_code: string,
) : Promise<string> {
    const response = await fetch(`/api/challenges/join_code/${join_code}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the challenge.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Challenge', {
        join_code
    });
    return await (response.text());
}

export async function updateChallenge(
    amplitude_callsite: string,
    challenge_id: string,
    challenge: CreateChallengeRequest,
    topic_key?: string,
    additional_logged_parameters: Object = {}
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/challenges/${challenge_id}${queryString}`, put(challenge));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating the challenge.', response);
    }

    logWithAmplitude(
        amplitude_callsite,
        'Challenge Updated',
        Object.assign(
            {},
            {
                challenge_id,
                topic_key,
            },
            additional_logged_parameters
        )
    );
}

export async function deleteChallenge(
    amplitude_callsite: string,
    challenge_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/challenges/${challenge_id}${queryString}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the challenge.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Challenge Deleted', {
        challenge_id,
        topic_key,
    });
}

export async function addChallenge(
    amplitude_callsite: string,
    topic_id: string,
    challenge: CreateChallengeRequest,
    topic_key?: string,
) {
    const challenge_response = await addChallengeInner(topic_id, challenge, topic_key);
    logWithAmplitude(amplitude_callsite, 'Challenge Added', {
        topic_id,
        topic_key,
        challenge_id: challenge_response._id.$oid,
    });
    return challenge_response;
}

async function addChallengeInner(
    topic_id: string,
    challenge: CreateChallengeRequest,
    topic_key?: string
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/topics/${topic_id}/challenges${queryString}`, post(challenge));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the challenge.', response);
    }

    return await (response.json() as Promise<CreateChallengeResponse>);
}


export function createChallengeRequestFromGetChallengeResponse(response: GetChallengeResponse): CreateChallengeRequest {
    return {
        is_live: response.is_live,
        is_registration_active: response.is_registration_active,
        challenge_type: response.challenge_type,
        name: response.name,
        description: response.description,
        terms: response.terms,
        start_time: response.start_time,
        end_time: response.end_time,
        locations: response.locations ?? [],
        rewards: response.rewards,
        receipt_auto_approve: response.receipt_auto_approve,
        receipt_settings: response.receipt_settings,
        enable_receipt_checkin: response.enable_receipt_checkin,
        button_text: response.button_text,
        should_verify_check_ins: response.should_verify_check_ins,
        should_verify_with_code: response.should_verify_with_code, // remove after migration
        enable_code_checkin: response.enable_code_checkin,
        photo_instructions: response.photo_instructions,
        should_show_special_offers: response.should_show_special_offers,
        hide_special_offers_until_checkin: response.hide_special_offers_until_checkin,
        can_view_challenge_after_end_date: response.can_view_challenge_after_end_date,
        show_leaderboard: response.show_leaderboard,
        show_photo_feed: response.show_photo_feed,
        default_tab: response.default_tab,
        checkin_frequency_quantity: response.checkin_frequency_quantity,
        checkin_frequency_recurrance: response.checkin_frequency_recurrance,
        wallet_card_id: response.wallet_card_id,
        geofence_radius_m: response.geofence_radius_m,
        enable_qr_checkin: response.enable_qr_checkin,
        photo_auto_approve: response.photo_auto_approve,
        enable_photo_checkin: response.enable_photo_checkin,
        allow_internal_notes: response.allow_internal_notes,
        activities: response.activities,
        should_request_user_images: response.should_request_user_images,
        registration_settings: response.registration_settings,
        scavenger_hunt_settings: response.scavenger_hunt_settings,
        trivia_settings: response.trivia_settings,
        join_code: response.join_code,
    };
}

/// ACTIVATED CHALLENGE APIS

export async function getActivatedChallengesForChallenge(
    amplitude_callsite: string,
    challenge_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ challenge_key: topic_key, challenge_id });
    const response = await fetch(`/api/activated_challenges/challenge${queryString}`, get());

    if (!response.ok) {
        return [];
    }

    logWithAmplitude(amplitude_callsite, 'Get Activated Challenge', {
        challenge_id
    });

    return await (response.json() as Promise<GetActivatedChallengeResponse[]>);
}

export async function getActivatedChallengesForAccount(
    amplitude_callsite: string,
    email: string,
) {
    const queryString = buildQueryString({ email });
    const response = await fetch(`/api/activated_challenges/account${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the activated challenges.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Activated Challenge', {
        email
    });

    return await (response.json() as Promise<GetActivatedChallengeResponse[]>);
}


export async function getActivatedChallenge(amplitude_callsite: string, activated_challenge_id: string, activated_challenge_key?: string, use_cache?: boolean) {
    const queryString = buildQueryString({ activated_challenge_key, use_cache });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the activated challenge.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get Activated Challenge');
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

export async function deleteActivatedChallenge(
    amplitude_callsite: string,
    activated_challenge_id: string
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while deleting the activated challenge.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Activated Challenge Deleted', {
        activated_challenge_id,
    });
}

export async function addActivatedChallenge(
    amplitude_callsite: string,
    email: string,
    challenge_id: string,
    phone_number?: string,
    display_name?: string,
    sms_opted_out?: boolean,
    additional_fields?: AdditionalActivatedChallengeFields[],
    topic_key?: string,
): Promise<CreateActivatedChallengeResponse> {
    const request: CreateActivatedChallengeRequest = createActivateChallengeRequest(email, challenge_id, phone_number, display_name, sms_opted_out, additional_fields);
    const challenge_response = await addActivatedChallengeInner(challenge_id, request, topic_key);
    logWithAmplitude(amplitude_callsite, 'Activated Challenge Added', {
        activated_challenge_id: challenge_response._id.$oid,
    });
    return challenge_response;
}

export function createActivateChallengeRequest(
    email: string,
    challenge_id: string,
    phone_number?: string,
    display_name?: string,
    sms_opted_out?: boolean,
    additional_fields?: AdditionalActivatedChallengeFields[],
): CreateActivatedChallengeRequest {
    const referral = getStoredReferralCode();
    const request: CreateActivatedChallengeRequest = {
        challenge_id: {'$oid': challenge_id},
        email,
        phone_number,
        display_name,
        sms_opted_out: sms_opted_out ?? false,
        additional_fields,
        referred_by: referral,
    };
    return request;
}

async function addActivatedChallengeInner(
    challenge_id: string,
    createActivatedChallengeRequest: CreateActivatedChallengeRequest,
    topic_key?: string
) {
    const queryString = buildQueryString({ challenge_id, topic_key });
    const response = await fetch(`/api/activated_challenges${queryString}`, post(createActivatedChallengeRequest));
    if (!response.ok) {
        await throwHttpError('An error occurred while creating the activated challenge.', response);
    }

    return await (response.json() as Promise<CreateActivatedChallengeResponse>);
}

export async function resendChallengeActivationEmail(
    challenge_id: string,
    email?: string,
    phone_number?: string,
) {
    const queryString = buildQueryString({ email, phone_number, challenge_id });
    const response = await fetch(`/api/activated_challenges/resend_activation${queryString}`, post({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while resending the activation.', response);
    }
}

export async function updateActivatedChallenge (
    activated_challenge_id: string,
    request: UpdateActivatedChallengeRequest,
    activated_challenge_key?: string,
) {
    const queryString = buildQueryString({ activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}${queryString}`, put(request));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating activated challenge.', response);
    }
}

export async function resetActivatedChallenge (
    activated_challenge_id: string,
    topic_id: string,
    activated_challenge_key?: string,
) {
    const queryString = buildQueryString({ activated_challenge_key, topic_id });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/reset${queryString}`, put({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while resetting the activated challenge.', response);
    }
}

export async function overrideActivatedChallengePayment (
    activated_challenge_id: string,
    topic_id: string,
    activated_challenge_key?: string,
) {
    const queryString = buildQueryString({ activated_challenge_key, topic_id });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/override_payment${queryString}`, put({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while overriding the activated challenge payment.', response);
    }
}

export async function remove_challenge_checkin (
    activated_challenge_id: string,
    checkin_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/checkin?checkin_id=${checkin_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while removing the challenge checkin.', response);
    }
}

export async function remove_activity_checkin (
    activated_challenge_id: string,
    activity_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/activity_checkin?activity_id=${activity_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while removing the activity checkin.', response);
    }
}

export async function add_challenge_checkin (
    challenge_checkin_details: CreateChallengeCheckin,
    activated_challenge_id: string,
    activated_challenge_key?: string,
) {
    getMainPage().errorToastElement?.showToast(await translationData.translatePhrase('Checking in... This may take a few seconds.'));
    const queryString = buildQueryString({ activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/checkin${queryString}`, post(challenge_checkin_details.method));
    if (!response.ok) {
        await throwHttpError('An error occurred while checking in.', response);
    }
    return await (response.json() as Promise<CreateCheckinResponse>);
}

export async function approve_location_checkin (
    activated_challenge_id: string,
    location_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/checkin_approve/${location_id}`, post({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while approving location checkin.', response);
    }
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

export async function reject_location_checkin (
    activated_challenge_id: string,
    location_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/checkin_reject/${location_id}`, post({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while rejecting location checkin.', response);
    }
}

export async function approve_activity_checkin (
    activated_challenge_id: string,
    activity_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/checkin_approve_activity/${activity_id}`, post({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while approving activity checkin.', response);
    }
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

export async function reject_activity_checkin (
    activated_challenge_id: string,
    activity_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/checkin_reject_activity/${activity_id}`, post({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while rejecting activity checkin.', response);
    }
}

export async function set_challenge_photo_visibility (
    activated_challenge_id: string,
    image_id: string,
    can_be_displayed: boolean,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/photos/${image_id}/visibility`, post({ can_be_displayed }));
    if (!response.ok) {
        await throwHttpError('An error occurred while updating photo visibility.', response);
    }
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

export async function add_challenge_admin_points(
    activated_challenge_id: string,
    points: number,
    description: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/admin_points`, post({description, value: points}));
    if (!response.ok) {
        await throwHttpError('An error occurred while adding admin points.', response);
    }
}


export async function remove_admin_points_from_activated_challenge(
    activated_challenge_id: string,
    admin_bonus_point_id: string,
) {
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/admin_points/${admin_bonus_point_id}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while removing admin points.', response);
    }
}

export async function get_challenge_location_qr_code(
    challenge_id: string,
    challenge_location_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/challenges/${challenge_id}/location_qr/${challenge_location_id ?? ''}${queryString}`, get());

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the challenge location qr code.', response);
    }
    return await (response.blob() );
}

export async function get_hunt_hint(
    activated_challenge_id: string,
    activated_challenge_key: string,
    challenge_location_id: string,
) {
    const queryString = buildQueryString({ challenge_location_id, activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/hint${queryString}`, put(emptyPut()));

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the hunt hint.', response
        );
    }
}

export async function get_hunt_give_up(
    activated_challenge_id: string,
    activated_challenge_key: string,
    challenge_location_id: string,
) {
    const queryString = buildQueryString({ challenge_location_id, activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/give_up${queryString}`, put(emptyPut()));

    if (!response.ok) {
        await throwHttpError('An error occurred while getting the hunt give up.', response);
    }
}

export async function send_challenge_event_mail(
    amplitude_callsite: string,
    challenge_id: string,
    message_details: ChallengeMessage,
    topic_key?: string,
) {
    const queryString = buildQueryString({ topic_key });
    const response = await fetch(`/api/challenges/${challenge_id}/send_mail${queryString}`, post(message_details));
    if (!response.ok) {
        await throwHttpError('An error occurred while sending the challenge event mail.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Send Challenge Event Mail', {
        challenge_id,
        topic_key,
        message_details
    });
}

export async function redeem_activated_challenge_reward(
    activated_challenge_id: string,
    activated_challenge_key: string | undefined,
    reward_id: string,
) {
    const queryString = buildQueryString({ activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/redeem/${reward_id}${queryString}`, post({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while redeeming the activated challenge reward.', response);
    }
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

export async function remove_activated_challenge_reward(
    activated_challenge_id: string,
    activated_challenge_key: string | undefined,
    reward_id: string,
) {
    const queryString = buildQueryString({ activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/redeem/${reward_id}${queryString}`, del());
    if (!response.ok) {
        await throwHttpError('An error occurred while removing the activated challenge reward.', response);
    }
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

export async function updateSavedLocation(point_id: string, activated_challenge_id: string, activated_challenge_key?: string) {
    const queryString = buildQueryString({ activated_challenge_key });
    const response = await fetch(`/api/activated_challenges/${activated_challenge_id}/save_location/${point_id}${queryString}`, put({}));
    if (!response.ok) {
        await throwHttpError('An error occurred while saving the location to activated challenge.', response);
    }
    return await (response.json() as Promise<GetActivatedChallengeResponse>);
}

// Challenge Duplication API
export async function duplicateChallengeToTopic(
    challenge_id: string,
    target_topic_id: string,
    topic_key?: string,
) {
    const queryString = buildQueryString({ target_topic_id, topic_key });
    const response = await fetch(`/api/challenges/${challenge_id}/duplicate${queryString}`, emptyPost());
    if (!response.ok) {
        await throwHttpError('An error occurred while duplicating the challenge to the target topic.', response);
    }
}

export async function getDuplicateableChallenges(
    organization_id: string,
    challenge_type: ChallengeType,
) : Promise<GetChallengeResponse[]> {
    const response = await fetch(`/api/organizations/${organization_id}/duplicateable_challenges?challenge_type=${challenge_type}`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while getting duplicateable challenges.', response);
    }
    return await (response.json() as Promise<GetChallengeResponse[]>);
}
